import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import type { LegalDoc, LegalDocVersion } from '@/mocks/legalCenter';
import { legalCenterDocs, LEGAL_COLORS, getActiveVersion } from '@/mocks/legalCenter';
import EditorModal from './components/EditorModal';
import HistoryModal from './components/HistoryModal';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function SuperAdminLegalCenter() {
  const [docs, setDocs] = useState<LegalDoc[]>(legalCenterDocs);
  const [selectedDoc, setSelectedDoc] = useState<LegalDoc>(docs[0]);
  const [editingDoc, setEditingDoc] = useState<LegalDoc | null>(null);
  const [historyDoc, setHistoryDoc] = useState<LegalDoc | null>(null);

  const stats = useMemo(() => {
    const total = docs.length;
    const withDrafts = docs.filter((d) => d.versions.some((v) => v.status === 'draft')).length;
    const lastPublished = docs.reduce((latest, d) => {
      const dDate = new Date(d.lastPublishedAt);
      return dDate > latest ? dDate : latest;
    }, new Date(0));
    return { total, withDrafts, lastPublished };
  }, [docs]);

  // --- Handlers ---
  const handleSaveVersion = (docId: number, newVersion: LegalDocVersion) => {
    setDocs((prev) =>
      prev.map((d) =>
        d.id === docId
          ? {
              ...d,
              currentVersion: newVersion.versionNumber,
              versions: [...d.versions, newVersion],
              updatedAt: new Date().toISOString(),
            }
          : d,
      ),
    );
    setEditingDoc(null);
    Swal.fire({
      icon: 'success',
      title: 'Borrador guardado',
      html: `<p style="font-size:13px">La <strong>v${newVersion.versionNumber}</strong> se guardó como borrador. Activala desde el historial cuando esté lista.</p>`,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3500,
      timerProgressBar: true,
      customClass: { popup: 'rounded-2xl text-sm', title: 'text-xs font-semibold' },
    });
  };

  const handleActivateVersion = (docId: number, versionNumber: number) => {
    setDocs((prev) =>
      prev.map((d) => {
        if (d.id !== docId) return d;
        const now = new Date().toISOString();
        return {
          ...d,
          currentVersion: versionNumber,
          lastPublishedAt: now,
          updatedAt: now,
          versions: d.versions.map((v) => {
            if (v.versionNumber === versionNumber) return { ...v, status: 'active' as const, publishedAt: now };
            if (v.status === 'active') return { ...v, status: 'archived' as const };
            return v;
          }),
        };
      }),
    );
    setHistoryDoc(null);
    Swal.fire({
      icon: 'success',
      title: 'Versión activada',
      html: `<p style="font-size:13px">La <strong>v${versionNumber}</strong> ahora es la versión vigente. Se refleja en el Centro Legal público.</p>`,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3500,
      timerProgressBar: true,
      customClass: { popup: 'rounded-2xl text-sm', title: 'text-xs font-semibold' },
    });
  };

  const selectedActive = getActiveVersion(selectedDoc);
  const selectedColor = LEGAL_COLORS[selectedDoc.color];

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex h-screen overflow-hidden">
        <SASidebar />

        {/* ── Left Panel: Document list ── */}
        <div className="w-full lg:w-[380px] xl:w-[440px] shrink-0 border-r border-background-200 flex flex-col bg-background-50">
          {/* Header */}
          <div className="shrink-0 px-4 py-4 border-b border-background-100">
            <div className="flex items-center justify-between mb-1">
              <div>
                <h2 className="text-foreground-950 font-bold text-sm">Centro Legal</h2>
                <p className="text-foreground-400 text-[10px] mt-0.5">
                  {stats.total} documentos · {stats.withDrafts} con borradores
                </p>
              </div>
              <Link
                to="/sa/dashboard"
                className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-foreground-500 text-sm"></i>
              </Link>
            </div>

            {/* Stats mini */}
            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-background-100">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                <span className="text-foreground-500 text-[10px] font-medium">
                  Última publicación: {formatDate(stats.lastPublished.toISOString())}
                </span>
              </div>
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {docs.map((doc) => {
              const active = getActiveVersion(doc);
              const color = LEGAL_COLORS[doc.color];
              const isSelected = selectedDoc.id === doc.id;
              const hasDraft = doc.versions.some((v) => v.status === 'draft');

              return (
                <div
                  key={doc.id}
                  onClick={() => setSelectedDoc(doc)}
                  className={`flex items-start gap-3 px-4 py-3.5 cursor-pointer transition-colors border-b border-background-50 ${
                    isSelected
                      ? 'bg-accent-50/70 border-l-[3px] border-l-accent-500'
                      : 'hover:bg-background-50 border-l-[3px] border-l-transparent'
                  }`}
                >
                  {/* Icon */}
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                    isSelected ? color.iconBg : 'bg-background-200'
                  }`}>
                    <i className={`${doc.icon} ${isSelected ? color.iconText : 'text-foreground-400'} text-sm`}></i>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`text-xs font-semibold truncate ${isSelected ? 'text-foreground-900' : 'text-foreground-800'}`}>
                        {doc.title}
                      </p>
                      {hasDraft && (
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"></span>
                      )}
                    </div>
                    <p className="text-foreground-400 text-[10px] mt-0.5 line-clamp-1">{doc.description}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-bold ${color.bg} ${color.text}`}>
                        <span className={`w-1 h-1 rounded-full ${color.dot}`}></span>
                        v{active.versionNumber} activa
                      </span>
                      <span className="text-foreground-300 text-[9px]">{formatDate(doc.lastPublishedAt)}</span>
                    </div>
                  </div>

                  {/* Quick draft indicator */}
                  {hasDraft && (
                    <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[8px] font-bold whitespace-nowrap shrink-0 mt-1">
                      Borrador
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="shrink-0 border-t border-background-100 px-4 py-2.5 flex items-center justify-between">
            <p className="text-foreground-300 text-[10px]">{docs.length} documentos</p>
            <Link
              to="/legal-center"
              target="_blank"
              className="text-foreground-400 text-[9px] font-medium hover:text-foreground-600 transition-colors cursor-pointer flex items-center gap-1"
            >
              <i className="ri-external-link-line text-[9px]"></i>
              Ver centro legal público
            </Link>
          </div>
        </div>

        {/* ── Right Panel: Detail ── */}
        <div className="hidden lg:flex flex-1 min-w-0 flex-col bg-background-100">
          {/* Header */}
          <div className="shrink-0 px-5 py-4 border-b border-background-200 bg-background-50 flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-10 h-10 rounded-xl ${selectedColor.iconBg} flex items-center justify-center shrink-0`}>
                <i className={`${selectedDoc.icon} ${selectedColor.iconText} text-lg`}></i>
              </div>
              <div className="min-w-0">
                <h3 className="text-foreground-950 font-bold text-sm truncate">{selectedDoc.title}</h3>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold ${selectedColor.bg} ${selectedColor.text}`}>
                    <span className={`w-1 h-1 rounded-full ${selectedColor.dot}`}></span>
                    v{selectedActive.versionNumber} activa
                  </span>
                  <span className="text-foreground-400 text-[10px]">
                    Publicada {formatDateTime(selectedDoc.lastPublishedAt)}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setEditingDoc(selectedDoc)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-edit-line text-sm"></i>
                Editar
              </button>
              <button
                onClick={() => setHistoryDoc(selectedDoc)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-background-200 bg-background-50 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-history-line text-sm"></i>
                Historial
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-3xl px-5 py-6">
              {/* Description */}
              <div className={`mb-6 p-4 rounded-xl border ${selectedColor.border} ${selectedColor.bg}`}>
                <p className={`${selectedColor.text} text-xs leading-relaxed`}>{selectedDoc.description}</p>
              </div>

              {/* Preview label */}
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 rounded-lg bg-background-200 flex items-center justify-center">
                  <i className="ri-eye-line text-foreground-500 text-xs"></i>
                </div>
                <p className="text-foreground-400 text-[9px] font-bold uppercase tracking-wider">Vista previa · v{selectedActive.versionNumber}</p>
              </div>

              {/* Content preview */}
              <div className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8">
                <div
                  className="prose prose-sm max-w-none text-foreground-700 legal-preview"
                  dangerouslySetInnerHTML={{ __html: selectedActive.content }}
                ></div>
              </div>

              {/* Version info */}
              <div className="mt-4 pt-4 border-t border-background-200 flex items-center justify-between text-foreground-400 text-[10px]">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line text-[9px]"></i>
                  Última edición: {selectedDoc.updatedBy}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line text-[9px]"></i>
                  {formatDateTime(selectedDoc.updatedAt)}
                </span>
              </div>

              {/* Spacer */}
              <div className="h-8"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {editingDoc && (
        <EditorModal
          doc={editingDoc}
          onClose={() => setEditingDoc(null)}
          onSave={handleSaveVersion}
        />
      )}

      {historyDoc && (
        <HistoryModal
          doc={historyDoc}
          onClose={() => setHistoryDoc(null)}
          onActivateVersion={handleActivateVersion}
        />
      )}

      {/* Mobile bottom hint */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-2.5 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">
          Super Admin · Centro Legal
        </p>
      </div>
    </div>
  );
}